import React from "react";
import "./MyIslandDecorPanel.css";

export default function MyIslandDecorPanel({
  catalog = [],
  placements = [],
  trainerNpcCatalog = [],
  ownedTrainerNpcs = [],
  gameCornerNpcCatalog = [],
  ownedGameCornerNpcs = [],
  selectedDecorId = null,
  isMobile = false,
  onSelectDecor,
  onRemovePlacement,
  onEnterRemoveMode,
  onBuyTrainerNpc,
  onAssignTrainerNpc,
  onMoveTrainerNpc,
  onBuyGameCornerNpc,
  onAssignGameCornerNpc,
  onMoveGameCornerNpc,
  onClose,
}) {
  const formatNumber = (value) => Number(value || 0).toLocaleString();

  const renderCostDisplay = ({ costType, amount, costItemImage, costItemName, voltageNote }) => {
    const type = String(costType || "");
    let iconSrc = "/items/pokeball.png";
    let iconAlt = costItemName || "Item";

    if (type === "coins") {
      iconSrc = "/items/coin.png";
      iconAlt = "Coins";
    } else if (type === "money") {
      iconSrc = "/items/money.png";
      iconAlt = "Money";
    } else if (costItemImage) {
      iconSrc = `/${costItemImage}`;
    }

    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span>{formatNumber(amount)}</span>
          <img
            src={iconSrc}
            alt={iconAlt}
            style={{ width: 14, height: 14, imageRendering: "pixelated" }}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/items/pokeball.png";
            }}
          />
        </span>
        {voltageNote ? <span style={{ opacity: 0.85 }}>• {voltageNote}</span> : null}
      </span>
    );
  };

  const renderDecorCost = (d) => {
    const k = String(d?.decor_key || "").trim();
    const voltageNote = k === "farmingshop"
      ? "50k voltage/day"
      : (k === "hut" || k === "junglehut" || k === "tinyhome")
        ? "20k voltage/day"
        : k === "research"
        ? "600k voltage/day"
        : k === "drill"
        ? "800k voltage/day"
        : "";

    if (k === "research") {
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span>{formatNumber(25000)}</span>
            <img
              src="/items/coin.png"
              alt="Coins"
              style={{ width: 14, height: 14, imageRendering: "pixelated" }}
            />
          </span>
          <span style={{ opacity: 0.7 }}>+</span>
          {renderCostDisplay({
            costType: d?.cost_type,
            amount: d?.cost_amount,
            costItemImage: d?.cost_item_image,
            costItemName: d?.cost_item_name,
          })}
          {voltageNote ? <span style={{ opacity: 0.85 }}>• {voltageNote}</span> : null}
        </span>
      );
    }

    if (k === "drill") {
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span>{formatNumber(45000)}</span>
            <img
              src="/items/coin.png"
              alt="Coins"
              style={{ width: 14, height: 14, imageRendering: "pixelated" }}
            />
          </span>
          <span style={{ opacity: 0.7 }}>+</span>
          {renderCostDisplay({
            costType: d?.cost_type,
            amount: d?.cost_amount,
            costItemImage: d?.cost_item_image,
            costItemName: d?.cost_item_name,
          })}
          {voltageNote ? <span style={{ opacity: 0.85 }}>• {voltageNote}</span> : null}
        </span>
      );
    }

    if (k === "batterycharger" || k === "bigbatterycharger") {
      const lightBallAmount = k === "batterycharger" ? 25 : 100;
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {renderCostDisplay({
            costType: d?.cost_type,
            amount: d?.cost_amount,
            costItemImage: d?.cost_item_image,
            costItemName: d?.cost_item_name,
          })}
          <span style={{ opacity: 0.7 }}>+</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span>{formatNumber(lightBallAmount)}</span>
            <img
              src="/items/lightball.png"
              alt="Light Ball"
              style={{ width: 14, height: 14, imageRendering: "pixelated" }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/items/pokeball.png";
              }}
            />
          </span>
          {voltageNote ? <span style={{ opacity: 0.85 }}>• {voltageNote}</span> : null}
        </span>
      );
    }

    return renderCostDisplay({
      costType: d?.cost_type,
      amount: d?.cost_amount,
      costItemImage: d?.cost_item_image,
      costItemName: d?.cost_item_name,
      voltageNote,
    });
  };

  const isUtility = (d) => {
    const cat = String(d?.category || "").toLowerCase();
    if (cat === "utility") return true;
    const k = String(d?.decor_key || "").trim();
    return (
      k === "pond" ||
      k === "soilbed" ||
      k === "spawn-point" ||
      k === "batterycharger" ||
      k === "bigbatterycharger"
    );
  };

  const isBuilding = (d) => {
    const cat = String(d?.category || "").toLowerCase();
    if (cat === "building") return true;
    const k = String(d?.decor_key || "").trim();
    return k === "farmingshop" || k === "hut" || k === "junglehut" || k === "tinyhome";
  };

  const isEnvironmental = (d) => {
    const cat = String(d?.category || "").toLowerCase();
    if (cat === "environmental") return true;
    const k = String(d?.decor_key || "").trim();
    return (
      k === "tree" ||
      k === "bigtree" ||
      k === "huge_tree" ||
      k === "exotic_tree" ||
      k === "flowers" ||
      k === "fountain" ||
      k === "post" ||
      k === "rock" ||
      k === "pot" ||
      k === "reciever" ||
      k === "boulder" ||
      k === "mossy_boulder" ||
      k === "wide_boulder" ||
      k === "big_boulder" ||
      k === "regularwiderock" ||
      k === "crater" ||
      k === "statue"
    );
  };

  const environmentalCatalog = (catalog || []).filter(isEnvironmental);
  const buildingsCatalog = (catalog || []).filter(isBuilding);
  const utilityCatalog = (catalog || []).filter(isUtility);
  const decorationCatalog = (catalog || []).filter((d) => !isEnvironmental(d) && !isBuilding(d) && !isUtility(d));

  const environmentalPlacedCount = (placements || []).reduce((acc, p) => (isEnvironmental(p) ? acc + 1 : acc), 0);
  const baseSlots = 8;
  const bonusSlots = Math.floor(environmentalPlacedCount / 10);
  const computedSlots = Math.min(60, baseSlots + bonusSlots);
  const spawnPointPlaced = (placements || []).some((p) => String(p?.decor_key || "") === "spawn-point");

  return (
    <div className={`decor-panel-container ${isMobile ? 'mobile' : 'desktop'}`}
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="decor-panel-header"
        style={isMobile ? { position: "sticky", top: 0, zIndex: 2 } : undefined}
      >
        <div className="decor-panel-title">Decorate My Island</div>
        <div className="decor-panel-actions">
          <button onClick={onEnterRemoveMode} className="decor-btn decor-btn-remove">
            Remove Mode
          </button>
          <button onClick={onClose} className="decor-btn decor-btn-close">
            Close
          </button>
        </div>
      </div>

      <div className={`decor-panel-content ${isMobile ? 'mobile' : 'desktop'}`}
        style={isMobile ? { flex: 1, minHeight: 0, overflow: "hidden" } : undefined}
      >
          <div className={`decor-section ${isMobile ? 'mobile' : ''}`}>
            <div className="decor-section-header">
              <div className="decor-section-title">Environmental</div>
              <div className="decor-section-meta">{`${environmentalPlacedCount} placed • ${computedSlots} slots`}</div>
            </div>
            <div className={`decor-section-content ${isMobile ? 'mobile' : 'desktop'}`}>
              {environmentalCatalog.length === 0 ? (
                <div className="decor-empty-state">No objects available.</div>
              ) : (
                environmentalCatalog.map((d) => {
                  const isSelected = Number(selectedDecorId) === Number(d.decor_id);
                  const isFlowers = String(d.decor_key || "") === "flowers" || String(d.sprite_path || "").includes("/flowers.png");
                  const isFountain = String(d.decor_key || "") === "fountain" || String(d.sprite_path || "").includes("/fountain.png");
                  const costNode = renderDecorCost(d);

                  return (
                    <button
                      key={d.decor_id}
                      onClick={() => onSelectDecor?.(d)}
                      className={`decor-item-card ${isSelected ? 'selected' : ''}`}
                    >
                      {isFlowers ? (
                        <div
                          className="decor-item-icon"
                          style={{
                            backgroundImage: `url(${d.sprite_path})`,
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "240px 48px",
                            backgroundPosition: "0px 0px",
                            animation: "flowersAnim 0.9s steps(5) infinite",
                            ["--flowers-sheet-end"]: "-240px",
                          }}
                        />
                      ) : isFountain ? (
                        <div
                          className="decor-item-icon"
                          style={{
                            backgroundImage: `url(${d.sprite_path})`,
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "240px 48px",
                            backgroundPosition: "0px 0px",
                            animation: "flowersAnim 1.05s steps(5) infinite",
                            ["--flowers-sheet-end"]: "-240px",
                          }}
                        />
                      ) : (
                        <img
                          src={d.sprite_path}
                          alt={d.name}
                          className="decor-item-icon"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/items/pokeball.png";
                          }}
                        />
                      )}
                      <div className="decor-item-info">
                        <div className="decor-item-name">{d.name}</div>
                        <div className="decor-item-cost">{costNode}</div>
                      </div>
                      <div className={`decor-item-status ${isSelected ? 'placing' : 'select'}`}>
                        {isSelected ? "Placing" : "Select"}
                      </div>
                    </button>
                  );
                })
              )}

              {buildingsCatalog.length > 0 && (
                <div>
                  <div className="decor-category-header">Buildings</div>
                  {buildingsCatalog.map((d) => {
                    const isSelected = Number(selectedDecorId) === Number(d.decor_id);
                    const isFlowers = String(d.decor_key || "") === "flowers" || String(d.sprite_path || "").includes("/flowers.png");
                    const isFountain = String(d.decor_key || "") === "fountain" || String(d.sprite_path || "").includes("/fountain.png");
                    const costNode = renderDecorCost(d);

                    return (
                      <button
                        key={d.decor_id}
                        onClick={() => onSelectDecor?.(d)}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 10px",
                          borderRadius: 10,
                          background: isSelected ? "rgba(59, 130, 246, 0.18)" : "rgba(255,255,255,0.06)",
                          border: isSelected ? "1px solid rgba(59, 130, 246, 0.65)" : "1px solid rgba(255,255,255,0.10)",
                          color: "#fff",
                          cursor: "pointer",
                          marginBottom: 8,
                          textAlign: "left",
                        }}
                      >
                        {isFlowers ? (
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              flexShrink: 0,
                              backgroundImage: `url(${d.sprite_path})`,
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "240px 48px",
                              backgroundPosition: "0px 0px",
                              imageRendering: "pixelated",
                              animation: "flowersAnim 0.9s steps(5) infinite",
                              ["--flowers-sheet-end"]: "-240px",
                            }}
                          />
                        ) : isFountain ? (
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              flexShrink: 0,
                              backgroundImage: `url(${d.sprite_path})`,
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "240px 48px",
                              backgroundPosition: "0px 0px",
                              imageRendering: "pixelated",
                              animation: "flowersAnim 1.05s steps(5) infinite",
                              ["--flowers-sheet-end"]: "-240px",
                            }}
                          />
                        ) : (
                          <img
                            src={d.sprite_path}
                            alt={d.name}
                            style={{ width: 36, height: 36, objectFit: "contain", objectPosition: "center", imageRendering: "pixelated", flexShrink: 0 }}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "/items/pokeball.png";
                            }}
                          />
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 800, fontSize: 13, lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</div>
                          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)", marginTop: 2 }}>{costNode}</div>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 900, color: isSelected ? "#93c5fd" : "rgba(255,255,255,0.65)" }}>
                          {isSelected ? "Placing" : "Select"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {utilityCatalog.length > 0 && (
                <div>
                  <div className="decor-category-header">Utility</div>
                  {utilityCatalog.map((d) => {
                    const isSelected = Number(selectedDecorId) === Number(d.decor_id);
                    const isFlowers = String(d.decor_key || "") === "flowers" || String(d.sprite_path || "").includes("/flowers.png");
                    const isFountain = String(d.decor_key || "") === "fountain" || String(d.sprite_path || "").includes("/fountain.png");
                    const isSpawnPoint = String(d?.decor_key || "") === "spawn-point";
                    const spawnDisabled = isSpawnPoint && spawnPointPlaced && !isSelected;
                    const costNode = renderDecorCost(d);

                    return (
                      <button
                        key={d.decor_id}
                        onClick={() => {
                          if (spawnDisabled) return;
                          onSelectDecor?.(d);
                        }}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 10px",
                          borderRadius: 10,
                          background: isSelected ? "rgba(59, 130, 246, 0.18)" : "rgba(255,255,255,0.06)",
                          border: isSelected ? "1px solid rgba(59, 130, 246, 0.65)" : "1px solid rgba(255,255,255,0.10)",
                          color: "#fff",
                          cursor: spawnDisabled ? "not-allowed" : "pointer",
                          marginBottom: 8,
                          textAlign: "left",
                          opacity: spawnDisabled ? 0.6 : 1,
                        }}
                        disabled={spawnDisabled}
                      >
                        {isFlowers ? (
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              flexShrink: 0,
                              backgroundImage: `url(${d.sprite_path})`,
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "240px 48px",
                              backgroundPosition: "0px 0px",
                              imageRendering: "pixelated",
                              animation: "flowersAnim 0.9s steps(5) infinite",
                              ["--flowers-sheet-end"]: "-240px",
                            }}
                          />
                        ) : isFountain ? (
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              flexShrink: 0,
                              backgroundImage: `url(${d.sprite_path})`,
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "240px 48px",
                              backgroundPosition: "0px 0px",
                              imageRendering: "pixelated",
                              animation: "flowersAnim 1.05s steps(5) infinite",
                              ["--flowers-sheet-end"]: "-240px",
                            }}
                          />
                        ) : (
                          <img
                            src={d.sprite_path}
                            alt={d.name}
                            style={{ width: 36, height: 36, objectFit: "contain", objectPosition: "center", imageRendering: "pixelated", flexShrink: 0 }}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "/items/pokeball.png";
                            }}
                          />
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 800, fontSize: 13, lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</div>
                          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)", marginTop: 2 }}>{costNode}</div>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 900, color: isSelected ? "#93c5fd" : "rgba(255,255,255,0.65)" }}>{isSelected ? "Placing" : "Select"}</div>
                      </button>
                    );
                  })}
                </div>
              )}

              {decorationCatalog.length > 0 && (
                <div>
                  <div className="decor-category-header">Decorations</div>
                  {decorationCatalog.map((d) => {
                    const isSelected = Number(selectedDecorId) === Number(d.decor_id);
                    const isFlowers = String(d.decor_key || "") === "flowers" || String(d.sprite_path || "").includes("/flowers.png");
                    const isFountain = String(d.decor_key || "") === "fountain" || String(d.sprite_path || "").includes("/fountain.png");
                    const costNode = renderDecorCost(d);

                    return (
                      <button
                        key={d.decor_id}
                        onClick={() => onSelectDecor?.(d)}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 10px",
                          borderRadius: 10,
                          background: isSelected ? "rgba(59, 130, 246, 0.18)" : "rgba(255,255,255,0.06)",
                          border: isSelected ? "1px solid rgba(59, 130, 246, 0.65)" : "1px solid rgba(255,255,255,0.10)",
                          color: "#fff",
                          cursor: "pointer",
                          marginBottom: 8,
                          textAlign: "left",
                        }}
                      >
                        {isFlowers ? (
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              flexShrink: 0,
                              backgroundImage: `url(${d.sprite_path})`,
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "240px 48px",
                              backgroundPosition: "0px 0px",
                              imageRendering: "pixelated",
                              animation: "flowersAnim 0.9s steps(5) infinite",
                              ["--flowers-sheet-end"]: "-240px",
                            }}
                          />
                        ) : isFountain ? (
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              flexShrink: 0,
                              backgroundImage: `url(${d.sprite_path})`,
                              backgroundRepeat: "no-repeat",
                              backgroundSize: "240px 48px",
                              backgroundPosition: "0px 0px",
                              imageRendering: "pixelated",
                              animation: "flowersAnim 1.05s steps(5) infinite",
                              ["--flowers-sheet-end"]: "-240px",
                            }}
                          />
                        ) : (
                          <img
                            src={d.sprite_path}
                            alt={d.name}
                            style={{ width: 36, height: 36, objectFit: "contain", objectPosition: "center", imageRendering: "pixelated", flexShrink: 0 }}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "/items/pokeball.png";
                            }}
                          />
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 800, fontSize: 13, lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</div>
                          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)", marginTop: 2 }}>{costNode}</div>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 900, color: isSelected ? "#93c5fd" : "rgba(255,255,255,0.65)" }}>{isSelected ? "Placing" : "Select"}</div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="decor-helper-text">
                Selecting an object closes this menu and starts placement mode.
              </div>

              <div className="trainer-npc-section">
                <div className="trainer-npc-header">
                  Trainer NPCs
                </div>

                <div className="trainer-npc-description">
                  Buy Trainer NPCs, then assign them to a hut (costs 20,000 voltage near a receiver). After assigning, you can move them anywhere.
                </div>

                <div className="trainer-npc-subsection">
                  <div className="trainer-npc-subsection-title">
                    Shop
                  </div>

                  {(trainerNpcCatalog || []).length === 0 ? (
                    <div className="decor-empty-state">No Trainer NPCs available.</div>
                  ) : (
                    (trainerNpcCatalog || []).map((t) => {
                      const costNode = renderCostDisplay({
                        costType: t?.cost_type,
                        amount: t?.cost_amount,
                        costItemImage: t?.cost_item_image,
                        costItemName: t?.cost_item_name,
                      });

                      return (
                        <div key={`tnpc-cat-${t?.trainer_id}`} className="trainer-npc-card">
                          <div className="trainer-npc-info">
                            <img
                              src={t?.sprite_sheet || "/items/pokeball.png"}
                              alt={t?.trainer_name || "Trainer"}
                              className="trainer-npc-sprite"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = "/items/pokeball.png";
                              }}
                            />
                            <div className="trainer-npc-details">
                              <div className="trainer-npc-name">
                                {t?.trainer_name || `Trainer ${t?.trainer_id}`}
                              </div>
                              <div className="trainer-npc-cost">{costNode}</div>
                            </div>
                          </div>

                          <button
                            onClick={() => onBuyTrainerNpc?.(t)}
                            className="trainer-npc-btn trainer-npc-btn-buy"
                          >
                            Buy
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="trainer-npc-subsection">
                  <div className="trainer-npc-subsection-title">
                    Owned
                  </div>

                  {(ownedTrainerNpcs || []).length === 0 ? (
                    <div className="decor-empty-state">You don't own any Trainer NPCs yet.</div>
                  ) : (
                    (ownedTrainerNpcs || []).map((o) => {
                      const assigned = Boolean(o?.hut_placement_id);
                      const placed = Boolean(o?.placed);
                      const statusText = !assigned ? "Unassigned" : (placed ? `Placed (${o?.x}, ${o?.y})` : "Assigned");

                      return (
                        <div key={`tnpc-owned-${o?.owned_npc_id}`} className="trainer-npc-card">
                          <div className="trainer-npc-info">
                            <img
                              src={o?.sprite_sheet || "/items/pokeball.png"}
                              alt={o?.trainer_name || "Trainer"}
                              className="trainer-npc-sprite"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = "/items/pokeball.png";
                              }}
                            />
                            <div className="trainer-npc-details">
                              <div className="trainer-npc-name">
                                {o?.trainer_name || `Trainer ${o?.trainer_id}`}
                              </div>
                              <div className="trainer-npc-status">{statusText}</div>
                            </div>
                          </div>

                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {!assigned ? (
                              <button
                                onClick={() => onAssignTrainerNpc?.(o)}
                                className="trainer-npc-btn trainer-npc-btn-assign"
                              >
                                Assign
                              </button>
                            ) : (
                              <button
                                onClick={() => onMoveTrainerNpc?.(o)}
                                className="trainer-npc-btn trainer-npc-btn-move"
                              >
                                Move
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="trainer-npc-section">
                <div className="trainer-npc-header">
                  Game Corner NPCs
                </div>

                <div className="trainer-npc-description">
                  Buy Game Corner NPCs to access features like Race Tracks, Daily Raffle, and more! Assign them to a hut (costs 20,000 voltage near a receiver), then place them on your island.
                </div>

                <div className="trainer-npc-subsection">
                  <div className="trainer-npc-subsection-title">
                    Shop
                  </div>

                  {(gameCornerNpcCatalog || []).length === 0 ? (
                    <div className="decor-empty-state">No Game Corner NPCs available.</div>
                  ) : (
                    (gameCornerNpcCatalog || []).map((gc) => {
                      const costNode = renderCostDisplay({
                        costType: gc?.cost_type,
                        amount: gc?.cost_amount,
                        costItemImage: gc?.cost_item_image,
                        costItemName: gc?.cost_item_name,
                      });

                      const npcTypeLabel = gc?.npc_type === "racetrack" ? "Race Track" :
                                          gc?.npc_type === "raffle" ? "Daily Raffle" :
                                          gc?.npc_type === "slots" ? "Slot Machine" : "Game Corner";

                      return (
                        <div key={`gcnpc-cat-${gc?.npc_id}`} className="trainer-npc-card">
                          <div className="trainer-npc-info">
                            <img
                              src={gc?.sprite_sheet || "/items/pokeball.png"}
                              alt={gc?.npc_name || "NPC"}
                              className="trainer-npc-sprite"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = "/items/pokeball.png";
                              }}
                            />
                            <div className="trainer-npc-details">
                              <div className="trainer-npc-name">
                                {gc?.npc_name || `NPC ${gc?.npc_id}`}
                              </div>
                              <div className="trainer-npc-cost">{costNode}</div>
                              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>
                                {npcTypeLabel}
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={() => onBuyGameCornerNpc?.(gc)}
                            className="trainer-npc-btn trainer-npc-btn-buy"
                          >
                            Buy
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="trainer-npc-subsection">
                  <div className="trainer-npc-subsection-title">
                    Owned
                  </div>

                  {(ownedGameCornerNpcs || []).length === 0 ? (
                    <div className="decor-empty-state">You don't own any Game Corner NPCs yet.</div>
                  ) : (
                    (ownedGameCornerNpcs || []).map((o) => {
                      const assigned = Boolean(o?.hut_placement_id);
                      const placed = Boolean(o?.placed);
                      const statusText = !assigned ? "Unassigned" : (placed ? `Placed (${o?.x}, ${o?.y})` : "Assigned");

                      const npcTypeLabel = o?.npc_type === "racetrack" ? "Race Track" :
                                          o?.npc_type === "raffle" ? "Daily Raffle" :
                                          o?.npc_type === "slots" ? "Slot Machine" : "Game Corner";

                      return (
                        <div key={`gcnpc-owned-${o?.owned_npc_id}`} className="trainer-npc-card">
                          <div className="trainer-npc-info">
                            <img
                              src={o?.sprite_sheet || "/items/pokeball.png"}
                              alt={o?.npc_name || "NPC"}
                              className="trainer-npc-sprite"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = "/items/pokeball.png";
                              }}
                            />
                            <div className="trainer-npc-details">
                              <div className="trainer-npc-name">
                                {o?.npc_name || `NPC ${o?.npc_id}`}
                              </div>
                              <div className="trainer-npc-status">{statusText}</div>
                              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>
                                {npcTypeLabel}
                              </div>
                            </div>
                          </div>

                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {!assigned ? (
                              <button
                                onClick={() => onAssignGameCornerNpc?.(o)}
                                className="trainer-npc-btn trainer-npc-btn-assign"
                              >
                                Assign
                              </button>
                            ) : (
                              <button
                                onClick={() => onMoveGameCornerNpc?.(o)}
                                className="trainer-npc-btn trainer-npc-btn-move"
                              >
                                Move
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={`decor-section ${isMobile ? 'mobile' : ''}`}>
            <div className="decor-section-header">
              <div className="decor-section-title">Placed</div>
            </div>
            <div className={`decor-section-content ${isMobile ? 'mobile' : 'desktop'}`}>
              {placements.length === 0 ? (
                <div className="decor-empty-state">No objects placed yet.</div>
              ) : (
                placements.map((p) => (
                  (() => {
                    const isFlowers = String(p.decor_key || "") === "flowers" || String(p.sprite_path || "").includes("/flowers.png");
                    const isFountain = String(p.decor_key || "") === "fountain" || String(p.sprite_path || "").includes("/fountain.png");
                    return (
                  <div key={p.placement_id} className="placed-item-card">
                    <div className="placed-item-left">
                    {isFlowers ? (
                      <div
                        className="decor-item-icon"
                        style={{
                          width: 32,
                          height: 32,
                          backgroundImage: `url(${p.sprite_path})`,
                          backgroundRepeat: "no-repeat",
                          backgroundSize: "160px 32px",
                          backgroundPosition: "0px 0px",
                          animation: "flowersAnim 0.9s steps(5) infinite",
                          ["--flowers-sheet-end"]: "-160px",
                        }}
                      />
                    ) : isFountain ? (
                      <div
                        className="decor-item-icon"
                        style={{
                          width: 32,
                          height: 32,
                          backgroundImage: `url(${p.sprite_path})`,
                          backgroundRepeat: "no-repeat",
                          backgroundSize: `${32 * 5}px 32px`,
                          backgroundPosition: "0px 0px",
                          animation: "flowersAnim 1.05s steps(5) infinite",
                          ["--flowers-sheet-end"]: `-${32 * 5}px`,
                        }}
                      />
                    ) : (
                      <img
                        src={p.sprite_path}
                        alt={p.name}
                        className="decor-item-icon"
                        style={{ width: 32, height: 32 }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = "/items/pokeball.png";
                        }}
                      />
                    )}
                    <div>
                      <div className="placed-item-name">{p.name}</div>
                      <div className="placed-item-coords">{`(${p.x}, ${p.y})`}</div>
                    </div>
                    </div>
                    <button
                      onClick={() => onRemovePlacement?.(p)}
                      className="placed-item-remove-btn"
                    >
                      Remove
                    </button>
                  </div>
                    );
                  })()
                ))
              )}
            </div>
          </div>
      </div>
    </div>
  );
}
